import apiSlice from "../api/apiSlice";

export const WEBHOOK_EVENTS = [
  "NEW_BASELINE",
  "BID_AGREEMENT",
  "NEW_FLEXIBILITY_REQUEST",
  "BID_SELECTION",
  "BID_ACTIVATION",
  "QUALIFICATION",
];

export const integrationsApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getIntegrations: builder.query({
      query: () => ({
        url: "/integrations",
        method: "GET",
        validateStatus: (response, result) => response.status === 200 && !result?.isError,
      }),
      providesTags: (result) => {
        if (result) {
          return [
            { type: "Integration", id: "LIST" },
            ...result.map((i) => ({ type: "Integration", id: i.id })),
          ];
        }
        return [{ type: "Integration", id: "LIST" }];
      },
    }),
    createIntegration: builder.mutation({
      query: (body) => ({
        url: "/integrations",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "Integration", id: "LIST" }],
    }),
    updateIntegration: builder.mutation({
      query: ({ integrationId, body }) => ({
        url: `/integrations/${integrationId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_, __, { integrationId }) => [
        { type: "Integration", id: "LIST" },
        { type: "Integration", id: integrationId },
      ],
    }),
    deleteIntegration: builder.mutation({
      query: (integrationId) => ({
        url: `/integrations/${integrationId}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Integration", id: "LIST" }],
    }),
    testIntegration: builder.mutation({
      query: (integrationId) => ({
        url: `/integrations/${integrationId}/test`,
        method: "POST",
      }),
      invalidatesTags: (_, __, integrationId) => [
        { type: "Integration", id: "LIST" },
        { type: "Integration", id: integrationId },
      ],
    }),
    getIntegrationServices: builder.query({
      query: (integrationId) => ({
        url: `/integrations/${integrationId}/gdbn/services`,
        method: "GET",
        validateStatus: (response, result) => response.status === 200 && !result?.isError,
      }),
      providesTags: (_, __, integrationId) => [{ type: "IntegrationServices", id: integrationId }],
    }),
    createWebhooks: builder.mutation({
      query: ({ integrationId, serviceId, webhooks }) => ({
        url: `/integrations/${integrationId}/gdbn/services/${serviceId}/webhooks`,
        method: "POST",
        body: webhooks,
      }),
      invalidatesTags: (_, __, { integrationId }) => [
        { type: "IntegrationServices", id: integrationId },
      ],
    }),
    updateWebhook: builder.mutation({
      query: ({ integrationId, webhookId, url }) => ({
        url: `/integrations/${integrationId}/gdbn/webhooks/${webhookId}`,
        method: "PATCH",
        body: { url },
      }),
      invalidatesTags: (_, __, { integrationId }) => [
        { type: "IntegrationServices", id: integrationId },
      ],
    }),
    deleteWebhook: builder.mutation({
      query: ({ integrationId, webhookId }) => ({
        url: `/integrations/${integrationId}/gdbn/webhooks/${webhookId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_, __, { integrationId }) => [
        { type: "IntegrationServices", id: integrationId },
      ],
    }),
    testWebhook: builder.mutation({
      query: ({ integrationId, url, body }) => ({
        url: `/integrations/${integrationId}/gdbn/webhooks/test`,
        method: "POST",
        body: { url, body },
      }),
    }),
    getServiceAuth: builder.query({
      query: ({ integrationId, serviceId }) => ({
        url: `/integrations/${integrationId}/gdbn/services/${serviceId}/auth`,
        method: "GET",
        validateStatus: (response, result) => response.status === 200 && !result?.isError,
      }),
      providesTags: (_, __, { integrationId, serviceId }) => [
        { type: "IntegrationServiceAuth", id: `${integrationId}:${serviceId}` },
      ],
    }),
    createServiceAuth: builder.mutation({
      query: ({ integrationId, serviceId, body }) => ({
        url: `/integrations/${integrationId}/gdbn/services/${serviceId}/auth`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_, __, { integrationId, serviceId }) => [
        { type: "IntegrationServiceAuth", id: `${integrationId}:${serviceId}` },
        { type: "IntegrationServices", id: integrationId },
      ],
    }),
    updateServiceAuth: builder.mutation({
      query: ({ integrationId, authMethodId, body }) => ({
        url: `/integrations/${integrationId}/gdbn/auth/${authMethodId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_, __, { integrationId, serviceId }) => [
        { type: "IntegrationServiceAuth", id: `${integrationId}:${serviceId}` },
        { type: "IntegrationServices", id: integrationId },
      ],
    }),
    deleteServiceAuth: builder.mutation({
      query: ({ integrationId, serviceId }) => ({
        url: `/integrations/${integrationId}/gdbn/services/${serviceId}/auth`,
        method: "DELETE",
      }),
      invalidatesTags: (_, __, { integrationId, serviceId }) => [
        { type: "IntegrationServiceAuth", id: `${integrationId}:${serviceId}` },
        { type: "IntegrationServices", id: integrationId },
      ],
    }),
    testServiceAuth: builder.mutation({
      query: ({ integrationId, body }) => ({
        url: `/integrations/${integrationId}/gdbn/auth/test`,
        method: "POST",
        body,
      }),
    }),
  }),
});

export const {
  useGetIntegrationsQuery,
  useCreateIntegrationMutation,
  useUpdateIntegrationMutation,
  useDeleteIntegrationMutation,
  useTestIntegrationMutation,
  useGetIntegrationServicesQuery,
  useCreateWebhooksMutation,
  useUpdateWebhookMutation,
  useDeleteWebhookMutation,
  useTestWebhookMutation,
  useGetServiceAuthQuery,
  useCreateServiceAuthMutation,
  useUpdateServiceAuthMutation,
  useDeleteServiceAuthMutation,
  useTestServiceAuthMutation,
} = integrationsApiSlice;
